import { apiOrigin } from '../utils/surface';

const INTERCOM_API = apiOrigin();

export interface VideoCallSession {
  id: string;
  call_id: string;
  property_id: number;
  unit_id: number;
  tenant_id: number;
  tenant_phone: string;
  visitor_name?: string;
  status: 'pending' | 'ringing' | 'answered' | 'declined' | 'ended' | 'failed';
  created_at: string;
  answered_at?: string;
  ended_at?: string;
  duration_seconds?: number;
  door_unlocked?: boolean;
}

/**
 * Initiate a video call via Telnyx Video Room.
 * Creates room, generates tokens, sends SMS to tenant.
 * Returns visitor token + room ID so visitor can join immediately.
 */
export async function initiateVideoCall(params: {
  property_id: number;
  unit_id: number;
  unit_number: string;
  property_name: string;
  tenant_id: number;
  visitor_name?: string;
}): Promise<{ room_id: string; visitor_token: string; tenant_url?: string; status: string }> {
  const r = await fetch(`${INTERCOM_API}/intercom/call`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tenant_id: params.tenant_id,
      unit_id: params.unit_id,
      visitor_name: params.visitor_name || 'A visitor',
      property_id: params.property_id,
      unit_number: params.unit_number,
      property_name: params.property_name,
    }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to start call');
  }
  return r.json();
}

/**
 * Get call session details (kept for backward compat)
 */
export async function getCallSession(call_id: string): Promise<VideoCallSession | null> {
  return null; // Sessions now managed via Telnyx rooms
}

/**
 * Update call session status (kept for backward compat)
 */
export async function updateCallStatus(
  call_id: string,
  status: VideoCallSession['status'],
  metadata?: { duration_seconds?: number; door_unlocked?: boolean }
): Promise<void> {
  // No-op — Telnyx manages room state
}

/**
 * Trigger door unlock via backend
 */
export async function unlockDoor(
  propertyId: number | string,
  tenantId?: number | string,
  opts?: { intercomConfigId?: string; roomName?: string },
): Promise<{ unlocked: boolean; message: string }> {
  const r = await fetch(`${INTERCOM_API}/intercom/unlock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      property_id: propertyId,
      tenant_id: tenantId,
      intercom_config_id: opts?.intercomConfigId,
      room_name: opts?.roomName,
    }),
  });
  return r.json();
}

export async function logDoorUnlock(call_id: string): Promise<void> {
  // Logging handled by backend
}
